import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { MailX, CheckCircle2, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import SEOHead from '@/components/marketplace/SEOHead';
import { Button } from '@/components/ui/button';

/**
 * One-click unsubscribe landing page. Works straight from an email client
 * without logging in (per-user token), and switches OFF announcements only —
 * receipts, inquiries, verification, moderation and password mail continue.
 */
const Unsubscribe = () => {
  const [params] = useSearchParams();
  const [state, setState] = useState('working'); // working | done | error
  const [email, setEmail] = useState('');
  const [category, setCategory] = useState('announcement');

  useEffect(() => {
    const user_id = params.get('u');
    const token = params.get('t');
    // `c` selects the category: announcements (default) or campaign reminders.
    const category = params.get('c') === 'campaign' ? 'campaign' : 'announcement';
    if (!user_id || !token) { setState('error'); return; }
    api.post('/announcements/unsubscribe', { user_id, token, category })
      .then((r) => { setEmail(r.data?.email || ''); setCategory(r.data?.category || category); setState('done'); })
      .catch(() => setState('error'));
  }, [params]);

  return (
    <div className='indus-container flex min-h-[55vh] items-center justify-center py-16' data-testid='unsubscribe-page'>
      <SEOHead title='Unsubscribe | IndusFact' robots='noindex,nofollow' />
      <div className='max-w-lg rounded-xl border bg-card p-8 text-center'>
        {state === 'working' && (
          <>
            <Loader2 className='mx-auto h-8 w-8 animate-spin text-muted-foreground' />
            <p className='mt-3 text-sm text-muted-foreground'>Updating your preferences…</p>
          </>
        )}
        {state === 'done' && (
          <>
            <CheckCircle2 className='mx-auto h-10 w-10 text-[hsl(var(--verified))]' />
            <h1 className='mt-3 text-xl font-semibold' data-testid='unsubscribe-success'>You&apos;re unsubscribed</h1>
            <p className='mt-2 text-sm text-muted-foreground'>
              {email
                ? <>We won&apos;t send {category === 'campaign' ? 'reminder emails' : 'product announcements'} to <strong>{email}</strong> any more. </>
                : `We won't send ${category === 'campaign' ? 'reminder emails' : 'product announcements'} any more. `}
              Inquiry notifications, payment receipts, moderation updates and password emails are unaffected
              {category === 'campaign' ? ', and product announcements are a separate setting' : ''}.
            </p>
            <div className='mt-5 flex flex-wrap justify-center gap-2'>
              <Button asChild size='sm'><Link to='/dashboard/email-preferences'>Manage all email preferences</Link></Button>
              <Button asChild size='sm' variant='outline'><Link to='/'>Back to IndusFact</Link></Button>
            </div>
          </>
        )}
        {state === 'error' && (
          <>
            <MailX className='mx-auto h-10 w-10 text-muted-foreground' />
            <h1 className='mt-3 text-xl font-semibold' data-testid='unsubscribe-error'>This link isn&apos;t valid</h1>
            <p className='mt-2 text-sm text-muted-foreground'>
              The unsubscribe link looks incomplete or has been altered. You can switch announcements off from your
              account instead.
            </p>
            <div className='mt-5 flex flex-wrap justify-center gap-2'>
              <Button asChild size='sm'><Link to='/dashboard/email-preferences'>Open email preferences</Link></Button>
              <Button asChild size='sm' variant='outline'><Link to='/'>Back to IndusFact</Link></Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Unsubscribe;
